import { Button, Modal, Form, Input, DatePicker } from "antd";
import React, { useState } from "react";
import AddClientLogo from "../../assets/images/ClentVerticalLogo.jpg";
import moment from "moment";
import UserAssignmentSelect, {
  toApiValue,
} from "../UserAssignmentSelect";
import { useIsAdmin } from "../../hooks/useEffectiveRole";

const fieldConfigs = {
  "income-tax": ["client_name", "username", "password", "dob"],
  GST: ["client_name", "gst_username", "password", "gstin"],
  TDS: ["client_name", "tds_username", "password", "tan_number"],
};

const AddClientModal = ({
  open,
  handleClose,
  handleSubmit,
  type = "",
  title,
}) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  // Role-gate via the shared hook (embed JWT claims take precedence inside
  // the /embed/* iframe). Non-admins (regular `user` AND `read_only`) don't
  // see "Assign to" — new clients land in the Shared pool by default.
  // Admins see the picker and can target any team member.
  const isAdmin = useIsAdmin();
  const isNonAdmin = !isAdmin;

  const handleFormSubmit = (values) => {
    if (values.dob) {
      values.dob = values.dob.format("YYYY-MM-DD");
    }
    // Non-admin: force an empty assignee list so the row lands in the
    // Shared pool. (BE's role-default for role=user is self-assign; an
    // explicit empty array overrides that to match the no-picker UX.)
    if (isNonAdmin) {
      values.assigned_user_ids = [];
    } else if ("assigned_user_ids" in values) {
      // Many-to-many: the picker hands back an array of user-ids.
      // toApiValue normalises it to an array ([] = Shared). Send it as
      // `assigned_user_ids`. When the picker rendered nothing
      // (single-user-org branch) the key is absent → BE applies the
      // role-default per spec.
      values.assigned_user_ids = toApiValue(values.assigned_user_ids);
    }
    // Drop any stray deprecated scalar so the payload only carries the
    // array shape the BE now prefers.
    delete values.assigned_user_id;
    handleSubmit(values);
    form.resetFields();
    setIsFormValid(false);
  };

  const handleValuesChange = () => {
    // Touch-check the ORIGINAL required portal fields only — the new
    // assigned_user_id field (added for §7.1) is optional and shouldn't
    // gate the Add button. Without this scope, isFieldsTouched(true)
    // would return false for an admin who skips assignment, blocking
    // submission even though every required field is filled.
    const requiredFields = fieldConfigs[type] || [];
    setIsFormValid(
      form.isFieldsTouched(requiredFields, true) &&
        !form.getFieldsError().some(({ errors }) => errors.length)
    );
  };

  return (
    <Modal
      title={`Add ${title} Client`}
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      // Width was 700 — too narrow once the Assign-to field renders a
      // selected user as a tag like "Full Name (long.email@example.com)".
      // 820 gives the form column enough room that a typical assignee
      // tag fits in full without falling into the "+1 ..." overflow.
      width={820}
    >
      <div className="w-full flex gap-6 p-4">
        {/* Image Section */}
        <div className="">
          <img
            src={AddClientLogo}
            draggable={false}
            style={{
              width: "280px",
            }}
            className="h-full rounded-lg"
          />
        </div>

        {/* Form Section */}
        <div className="flex-1">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFormSubmit}
            onValuesChange={handleValuesChange}
          >
            {fieldConfigs[type]?.map((field) => (
              <Form.Item
                key={field}
                label={
                  field == "tan_number"
                    ? "TAN"
                    : field == "gstin"
                    ? "GSTIN"
                    : field == "gst_username"
                    ? "GST Username"
                    : field
                        .replace("_", " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase())
                }
                name={field}
                rules={[
                  {
                    required: true,
                    message: `Please enter ${field}`,
                  },
                ]}
              >
                {field === "dob" ? (
                  <DatePicker format="DD/MM/YYYY" className="w-full" />
                ) : field === "password" ? (
                  <Input.Password
                    placeholder={`Enter ${field
                      .replace("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}`}
                  />
                ) : (
                  <Input
                    placeholder={`Enter ${field
                      .replace("_", " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}`}
                  />
                )}
              </Form.Item>
            ))}

            {/* User assignment (§7.1). Renders nothing in single-user
                orgs — the component self-suppresses. Optional field; if
                left empty, BE applies role-default (regular user → self,
                admin → Shared) per spec §5.2.
                Also hidden entirely for non-admins — those users don't
                need to choose; the submit handler forces Shared. */}
            {!isNonAdmin && (
              <Form.Item name="assigned_user_ids" label="Assign to">
                {/* Scope picker to members who can actually use this
                    portal. `type` is one of "income-tax" / "GST" / "TDS"
                    per `fieldConfigs` above — UserAssignmentSelect's
                    PORTAL_TO_FLAG map normalises all three flavours. */}
                <UserAssignmentSelect portal={type} />
              </Form.Item>
            )}

            <div className="text-right mt-4">
              <Button
                type="primary"
                htmlType="submit"
                className="min-w-[100px] w-full"
                disabled={!isFormValid}
              >
                Add Client
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default AddClientModal;
